import React from "react";
import { useTranslation } from "react-i18next";
import Avatar from "@/components/ui/avatar";
import ProgressBar from "@/components/ui/progress-bar";
import Chip from "@/components/ui/chip";
import { ArrowRightLong } from "@/components/icons";
import type { PollAttendee } from "../../types/polls.types";
import styles from "./AttendeeCard.module.scss";

export interface AttendeeCardProps {
  attendee: PollAttendee;
  onViewAnswers?: (attendee: PollAttendee) => void;
  className?: string;
}

const AttendeeCard: React.FC<AttendeeCardProps> = ({
  attendee,
  onViewAnswers,
  className = "",
}) => {
  const { t } = useTranslation();

  const hasAnswers = attendee.answeredCount > 0;

  return (
    <div className={`${styles.attendeeCard} ${className}`}>
      {attendee.status === "katilmadi" && (
        <div className={styles.statusBadge}>
          <Chip type="error" size="small">
            {t("polls.attendees.notParticipated")}
          </Chip>
        </div>
      )}

      <div className={styles.cardHeader}>
        <Avatar alt={attendee.name} color="primary" width={72} height={72} />
        <div className={styles.attendeeInfo}>
          <h3 className={styles.attendeeName}>{attendee.name}</h3>
          <p className={styles.attendeeTitle}>{attendee.title}</p>
          {attendee.educationName && (
            <p className={styles.attendeeEducation}>
              <span className={styles.attendeeEducationLabel}>
                {t("polls.attendees.education")}:
              </span>{" "}
              {attendee.educationName}
            </p>
          )}
        </div>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.progressSection}>
          <span className={styles.progressLabel}>
            {t("polls.attendees.answerRate")}
          </span>
          <ProgressBar
            value={attendee.percentage}
            showLabel={false}
            size="small"
          />
          <div className={styles.progressStats}>
            <span className={styles.progressCount}>
              ({attendee.answeredCount}/{attendee.totalQuestions})
            </span>
            <span className={styles.progressPercent}>%{attendee.percentage}</span>
          </div>
        </div>
      </div>

      <div className={styles.cardFooter}>
        <button
          type="button"
          className={`${styles.viewAnswersBtn} ${!hasAnswers ? styles.disabled : ""}`}
          onClick={() => hasAnswers && onViewAnswers?.(attendee)}
          disabled={!hasAnswers}
        >
          <span>{t("polls.attendees.viewAnswers")}</span>
          <ArrowRightLong
            width={20}
            height={20}
            color={hasAnswers ? "#e7212b" : "#c4c9d4"}
          />
        </button>
      </div>
    </div>
  );
};

export default AttendeeCard;
